import uuid from 'uuid';
import { AccessType, EximModules, InquiryType, ModesOfService } from 'src/Library/Constants';
import { isValidArray } from 'src/Library/Helper';
import moment from 'moment';
import { MOVEMENT_TYPES } from '../../Container/ContainerUtils';

export class NewShipmentHelper {
  static incotemDisabled(editData, disableBooking) {
    if (!disableBooking) {
      return false;
    }
    return Boolean(editData?.incoterm);
  }

  static carrierDisabled(editData, disableBooking) {
    const hasCarrier = Boolean(editData?.bidDetails?.carrier || editData?.shippingLineId || editData?.carrierCode);
    const hasTracking = Boolean(editData?.containerList?.length || editData?.blNumber || editData?.bookingNumber);
    return Boolean(hasCarrier && hasTracking);
  }

  static movementTypeDisabled(editData, disableBooking) {
    if (!disableBooking) {
      return false;
    }
    return Boolean(editData?.inquiryType || editData?.movementType);
  }

  static originPortDisabled(editData, disableBooking) {
    if (!disableBooking) {
      return false;
    }
    return Boolean(editData?.origin?.code || editData?.originPortCode);
  }

  static destinationPortDisabled(editData, disableBooking) {
    if (!disableBooking) {
      return false;
    }
    return Boolean(editData?.destination?.code || editData?.destinationPortCode);
  }

  static originCountryDisabled(editData, disableBooking) {
    if (!disableBooking) {
      return false;
    }
    const originCountryCode =
      editData?.origin?.countryCode ||
      editData?.originPortCountryCode ||
      editData?.originCountryCode;

    return Boolean(originCountryCode);
  }

  static destinationCountryDisabled(editData, disableBooking) {
    if (!disableBooking) {
      return false;
    }
    const destinationCountryCode =
      editData?.destination?.countryCode ||
      editData?.destinationPortCountryCode ||
      editData?.destinationCountryCode;

    return Boolean(destinationCountryCode);
  }

  static cargoDisabled(editData, disableBooking) {
    if (!disableBooking) {
      return false;
    }
    return Boolean(editData?.inquiryCargoType || editData?.cargoType);
  }

  static stackableDisabled(editData, disableBooking) {
    if (!disableBooking) {
      return false;
    }
    return Boolean(editData?.stackable);
  }

  static serviceDisabled(services, disableBooking) {
    if (!disableBooking) {
      return false;
    }
    return Boolean(services?.length);
  }

  static pickupDisabled(editData, disableBooking) {
    if (!disableBooking) {
      return false;
    }
    return Boolean(editData?.pickupAndDrop);
  }

  static locationDisabled(location, disableBooking) {
    if (!disableBooking) {
      return false;
    }
    return Boolean(location?.length);
  }

  static tncDisabled(editData, disableBooking) {
    if (!disableBooking) {
      return false;
    }
    return Boolean(editData?.tnc);
  }

  static cargoProductDisabled(editData, disableBooking) {
    if (!disableBooking) {
      return false;
    }
    return Boolean(editData?.inquiryProductType || editData?.cargoProduct);
  }

  static containerDisabled(editData, disableBooking) {
    if (!disableBooking) {
      return false;
    }
    if (editData?.inquiryContainerDetails?.length || editData?.packagingDetails?.length) {
      return true;
    }
    return false;
  }

  static productDisabled(editData, disableBooking) {
    if (!disableBooking) {
      return false;
    }
    const disable = editData?.productDetails?.some(prod => !!prod?.key);
    return disable;
  }

  static stakeholderDisabled(editData, disableBooking) {
    if (!disableBooking) {
      return false;
    }
    if (
      editData?.supplierId ||
      editData?.customerId ||
      editData?.chaId ||
      editData?.ffId ||
      editData?.transporterId ||
      editData?.cfsContractorId ||
      editData?.icdContractorId ||
      editData?.breakBulkId ||
      editData?.surveyorId ||
      editData?.interCartingId
    ) {
      return true;
    }
    return false;
  }

  static getOriginPort(editData) {
    if (editData?.origin?.code) {
      return {
        ...editData.origin,
        key: editData.origin?.code,
        label: `${editData?.origin?.name} [${editData?.origin?.code}]`,
      };
    }
    if (editData?.originPortCode) {
      return {
        code: editData?.originPortCode,
        country: editData?.originPortCountryName || editData?.originPortCountry || editData?.originCountryName,
        countryCode: editData?.originPortCountryCode || editData?.originCountryCode,
        countryName: editData?.originPortCountryName || editData?.originPortCountry || editData?.originCountryName,
        key: editData?.originPortCode,
        label: `${editData?.originPortName} [${editData?.originPortCode}]`,
        name: editData?.originPortName,
        type: editData?.originPortType,
      };
    }
    return undefined;
  }

  static getDestinationPort(editData) {
    if (editData?.destination?.code) {
      return {
        ...editData.destination,
        key: editData.destination?.code,
        label: `${editData?.destination?.name} [${editData?.destination?.code}]`,
      };
    }
    if (editData?.destinationPortCode) {
      return {
        code: editData?.destinationPortCode,
        country: editData?.destinationPortCountryName || editData?.destinationPortCountry || editData?.destinationCountryName,
        countryCode: editData?.destinationPortCountryCode || editData?.destinationCountryCode,
        countryName: editData?.destinationPortCountryName || editData?.destinationPortCountry || editData?.destinationCountryName,
        key: editData?.destinationPortCode,
        label: `${editData?.destinationPortName} [${editData?.destinationPortCode}]`,
        name: editData?.destinationPortName,
        type: editData?.destinationPortType,
      };
    }
    return undefined;
  }

  static getOriginCountry(editData) {
    const key =
      editData?.origin?.countryCode ||
      editData?.originPortCountryCode ||
      editData?.originCountryCode;
    const label =
      editData?.origin?.countryName ||
      editData?.originPortCountryName ||
      editData?.originPortCountry ||
      editData?.originCountryName;

    if (!key) {
      return undefined;
    }

    return {
      key,
      label,
    };
  }

  static getDestinationCountry(editData) {
    const key =
      editData?.destination?.countryCode ||
      editData?.destinationPortCountryCode ||
      editData?.destinationCountryCode;
    const label =
      editData?.destination?.countryName ||
      editData?.destinationPortCountryName ||
      editData?.destinationPortCountry ||
      editData?.destinationCountryName;

    if (!key) {
      return undefined;
    }

    return {
      key,
      label,
    };
  }

  static getAccessInitial(editData) {
    if (editData?.accessorType || editData?.accessType) {
      return {
        key: editData.accessorType || editData?.accessType,
      };
    }
    return { key: AccessType.PUBLIC };
  }

  static getOriginPolCode(editData) {
    if (editData?.originPolCode) {
      return {
        key: editData?.originPolCode,
        label: editData?.originPolName ?
          `${editData?.originPolName} [${editData?.originPolCode}]` :
          editData?.originPolCode,
      };
    }
    return undefined;
  }

  static getDestinationPodCode(editData) {
    if (editData?.destinationPodCode) {
      return {
        key: editData?.destinationPodCode,
        label: editData?.destinationPodName ?
          `${editData?.destinationPodName} [${editData?.destinationPodCode}]` :
          editData?.destinationPodCode,
      };
    }
    return undefined;
  }

  static getTeamIds(editData) {
    if (editData?.teamId) {
      return {
        key: editData?.teamId,
        label: editData?.teamName,
      };
    }
    if (editData?.teamList) {
      return editData?.teamList?.map(team => ({
        key: team.id,
        label: team.name,
      }));
    }
    if (editData?.teamDetails) {
      const teamAccessorDetails = editData?.teamDetails?.map(team => ({
        key: team.teamId?.toString(),
        label: team.teamName?.toString(),
      }));
      teamAccessorDetails.filter((team) => team.key && team.label);
      return editData?.accessType === AccessType.TEAMS ? teamAccessorDetails : [];
    }
    if (editData?.teamName){
      return editData?.teamName;
    }
    return undefined;
  }

  static getCustomFields(editData) {
    if (isValidArray(editData?.customFields)) {
      return editData?.customFields.map(field => {
        const fieldType = field?.type?.toLowerCase() || 'string';
        const fieldValue = field?.value;
        return {
          dbKey: field.dbKey,
          value: (fieldType === 'date' && fieldValue) ? moment(fieldValue) : fieldValue
        };
      });
    }
    return undefined;
  }

  /**
   * 
   * @param idsList list of ids
   * @param namesList list of names
   * @returns list of object of format ```{ key: idKey, label: nameValue }``` for customerIds and suppliersIds list
   */
  static getFormattedList(idsList, namesList){
    if (!( Array.isArray(idsList) && Array.isArray(namesList) )){
      return undefined;
    }
    if ( idsList.length !== namesList.length ){
      return [];
    }
    return idsList.map((id, indx) => { return { "key": id, "label": namesList[indx] } });
  }

  static getPurchaseOrderNumberList(editData: any): string[] | undefined {
    if (editData?.purchaseOrderNumberList) {
      return editData.purchaseOrderNumberList;
    }
    if (editData?.purchaseOrderNumber) {
      return [editData.purchaseOrderNumber];
    }
    if (editData?.bookingNumber) {
      return [editData.bookingNumber];
    }
    return undefined;
  }

  static convertEditdata(editData, disableBooking) {
    const stakeholderAsSupplier = editData?.type === InquiryType.IMPORT && editData?.stakeholder?.id ? editData.stakeholder : undefined;
    return {
      id: editData?.id,
      sourceObjectId: editData?.sourceObjectId,
      sourceObjectType: editData?.sourceObjectType,
      bookingDisabled: Boolean(editData?.id),
      mode: editData?.inquiryMode || editData?.mode,
      modeDisabled: Boolean(editData?.inquiryMode || editData?.mode),
      incoterm: editData?.incoterm || undefined,
      incotermDisabled: this.incotemDisabled(editData, disableBooking),
      carrierCode: editData?.bidDetails?.carrier || editData?.shippingLineId || editData?.carrierCode,
      carrierDisabled: this.carrierDisabled(editData, disableBooking),
      type: editData?.inquiryType || editData?.movementType || editData?.type,
      movementTypeDisabled: this.movementTypeDisabled(editData, disableBooking),
      originPortCode: this.getOriginPort(editData),
      originPortDisabled: this.originPortDisabled(editData, disableBooking),
      originCountryCode: this.getOriginCountry(editData),
      originCountryDisabled: this.originCountryDisabled(editData, disableBooking),
      originAddress: editData?.originAddress,
      originAddressDisabled: Boolean(editData?.originAddress && disableBooking),
      destinationPortCode: this.getDestinationPort(editData),
      destinationPortDisabled: this.destinationPortDisabled(editData, disableBooking),
      destinationCountryCode: this.getDestinationCountry(editData),
      destinationCountryDisabled: this.destinationCountryDisabled(editData, disableBooking),
      destinationAddress: editData?.destinationAddress,
      destinationAddressDisabled: Boolean(editData?.destinationAddress && disableBooking),
      customer: editData?.customer,
      stakeholder: editData?.stakeholder,
      teamIds: this.getTeamIds(editData),
      originStuffingAddress: editData?.origin?.stuffingDestuffingAddress,
      destinationStuffingAddress: editData?.destination?.stuffingDestuffingAddress,
      cargoType: editData?.cargoType || editData?.inquiryCargoType || undefined,
      cargoSubtype: editData?.inquiryProductType || editData?.cargoProduct || editData?.cargoSubtype || undefined,
      cargoDisabled: this.cargoDisabled(editData, disableBooking),
      cargoProductDisabled: this.cargoProductDisabled(editData, disableBooking),
      stackableDisabled: this.stackableDisabled(editData, disableBooking),
      containerDisabled: this.containerDisabled(editData, disableBooking),
      containerList: (editData?.inquiryContainerDetails || editData?.packagingDetails)?.map(item => ({
        ...item,
        id: uuid(),
        type: item.type,
        size: item.size,
        temperature: item.temperature,
        count: item.count,
        minVolumeUnit: item?.minVolumeUnit,
        dimensionUnit: item?.dimensionUnit,
        totalWeightUnit: item?.weight?.unit || item?.totalWeightUnit || item?.grossWeightUnit,
        totalWeight: item?.weight?.value || item?.totalWeight || item?.grossWeight,
      })),
      productList: editData?.productDetails?.flatMap(item => {
        // If key is an array, create separate product entries
        if (Array.isArray(item?.key) && item.key.length > 0) {
          return item.key.map(keyItem => ({
            id: uuid(),
            key: keyItem?.id,
            unit: keyItem?.unit,
            type: keyItem?.type || item?.type, 
            quantity: keyItem?.count,
            internalName: keyItem?.product_name || keyItem?.internalName || keyItem?.name || item?.product_name || item?.internalName || item?.name,
            cases: keyItem?.cases || item?.cases,
            skuClientNumber: keyItem?.sku_client_number || item?.sku_client_number
          }));
        }
        
        // If key is not an array, return single item
        return [{
          id: uuid(),
          key: item?.key || item?.id,
          unit: item?.unit,
          type: item?.type,
          quantity: item?.count,
          internalName: item?.product_name || item?.internalName || item?.name,
          cases: item?.cases,
          skuClientNumber: item?.sku_client_number
        }];
      }),
      productDisabled: this.productDisabled(editData, disableBooking),
      originServices: editData?.originServices,
      destinationServices: editData?.destinationServices,
      originServicesDisabled: this.serviceDisabled(editData?.originServices, disableBooking),
      destinationServicesDisabled: this.serviceDisabled(editData?.destinationServices, disableBooking),
      pickupAndDrop: editData?.pickupAndDrop,
      pickupDisabled: this.pickupDisabled(editData, disableBooking),
      stuffingLocation: editData?.stuffing || undefined,
      deStuffingLocation: editData?.destuffing || undefined,
      stuffingDisabled: this.locationDisabled(editData?.stuffing, disableBooking),
      deStuffingDisabled: this.locationDisabled(editData?.stuffing, disableBooking),
      originLocationType: editData?.originLocationType,
      originLocationId: { key: editData?.originLocationId },
      destinationLocationType: editData?.destinationLocationType,
      destinationLocationId: { key: editData?.destinationLocationId },
      termsAndConditions: editData?.termsAndConditions,
      tncDisabled: this.tncDisabled(editData, disableBooking),
      stakeholderDisabled: this.stakeholderDisabled(editData, disableBooking),
      ffId: editData?.ffId ? { key: editData?.ffId, label: editData?.ffName } : undefined,
      chaId: editData?.chaId ? { key: editData?.chaId, label: editData?.chaName } : undefined,
      parentId: editData?.parentId ? { key: editData?.parentId, label: editData?.parentName } : undefined,
      childId: editData?.childId ? { key: editData?.childId, label: editData?.childName } : undefined,
      transporterId: editData?.transporterId ? { key: editData?.transporterId, label: editData?.transporterName } : undefined,
      cfsContractorId: editData?.cfsContractorId ? { key: editData?.cfsContractorId, label: editData?.cfsContractorName } : undefined,
      icdContractorId: editData?.icdContractorId ? { key: editData?.icdContractorId, label: editData?.icdContractorName } : undefined,
      breakBulkId: editData?.breakBulkId ? { key: editData?.breakBulkId, label: editData?.breakBulkName } : undefined,
      surveyorId: editData?.surveyorId ? { key: editData?.surveyorId, label: editData?.surveyorName } : undefined,
      interCartingId: editData?.interCartingId ? { key: editData?.interCartingId, label: editData?.interCartingName } : undefined,
      shipperId: editData?.shipperId ? { key: editData?.shipperId, label: editData?.shipperName } : undefined,
      customerId: editData?.customerId ? { key: editData?.customerId, label: editData?.customerName } : undefined,
      supplierId: editData?.supplierId
        ? { key: editData.supplierId, label: editData.supplierName }
        : (stakeholderAsSupplier ? { key: stakeholderAsSupplier.id, label: stakeholderAsSupplier.name } : undefined),
      customerIds: editData?.customerIds ? this.getFormattedList(editData?.customerIds, editData?.customerNames) : undefined,
      supplierIds: editData?.supplierIds
        ? this.getFormattedList(editData.supplierIds, editData.supplierNames)
        : (stakeholderAsSupplier ? this.getFormattedList([stakeholderAsSupplier.id], [stakeholderAsSupplier.name]) : undefined),

      accessType: this.getAccessInitial(editData),
      purchaseOrderNumberList: this.getPurchaseOrderNumberList(editData),
      salesOrderNumber: editData?.salesOrderNumber || undefined,
      // Handle both singular and plural formats for invoice numbers
      invoiceNumberList: editData?.invoiceNumberList || 
        (editData?.invoiceNumber ? [editData?.invoiceNumber] : undefined),
      referenceNumber: editData?.referenceNumber || undefined,
      watcherList: editData?.watcherList || undefined,
      originPolCode: this.getOriginPolCode(editData),
      polDisabled: Boolean(this.getOriginPolCode(editData)),
      destinationPodCode: this.getDestinationPodCode(editData),
      podDisabled: Boolean(this.getDestinationPodCode(editData)),
      containerNumber: editData?.containerNumber,
      bookingNumber: editData?.bookingNumber,
      blNumber: editData?.blNumber,
      blNumberDisabled: Boolean(editData?.blNumber),
      clientReferenceNumber: editData?.clientReferenceNumber || editData?.internalReferenceNumber,
      ctShipmentId: editData?.ctShipmentId,
      documents: editData?.documents,
      customFields: this.getCustomFields(editData),
      plantId: editData?.plantId ? { 
        key: editData?.plantId,
        label: editData?.plantName || editData?.plantDetails?.name
      } : undefined,
      stackable: editData?.isCargoStackable,
      contractNumber: editData?.contractNumber,
      ...(((editData?.inquiryMode || editData?.mode) === ModesOfService.DOMESTIC || (editData?.inquiryMode || editData?.mode) === ModesOfService.RPTL) && {
        type: (editData?.inquiryMode || editData?.mode) === ModesOfService.DOMESTIC ? MOVEMENT_TYPES.TRADE : editData?.type,
        transporterId: editData?.transporterId
          ? { key: editData?.transporterId, label: editData?.transporterName }
          : (editData?.ffId ? { key: editData?.ffId, label: editData?.ffName } : undefined),
        stuffingLocation: `${editData?.originLocationDetails?.name} (${editData?.originLocationDetails?.referenceNumber || editData?.originLocationDetails?.reference_number})`,
        deStuffingLocation: `${editData?.destinationLocationDetails?.name} (${editData?.destinationLocationDetails?.referenceNumber || editData?.destinationLocationDetails?.reference_number})`,
        stuffingDisabled: true,
        deStuffingDisabled: true,
        originAddress: [
          editData?.originLocationDetails?.address, 
          editData?.originLocationDetails?.landmark, 
          editData?.originLocationDetails?.city, 
          editData?.originLocationDetails?.state, 
          editData?.originLocationDetails?.countryName || editData?.originLocationDetails?.country_name, 
          editData?.originLocationDetails?.pincode
        ].filter((val) => val).join(', '),
        destinationAddress: [
          editData?.destinationLocationDetails?.address, 
          editData?.destinationLocationDetails?.landmark, 
          editData?.destinationLocationDetails?.city, 
          editData?.destinationLocationDetails?.state, 
          editData?.destinationLocationDetails?.countryName || editData?.destinationLocationDetails?.country_name, 
          editData?.destinationLocationDetails?.pincode
        ].filter((val) => val).join(', '),
        originLocationId: { key: editData?.originLocationDetails?.id },
        destinationLocationId: { key: editData?.destinationLocationDetails?.id },
        originCountryCode: { key: editData?.originLocationDetails?.countryCode || editData?.originLocationDetails?.country_code, label: editData?.originLocationDetails?.countryName || editData?.originLocationDetails?.country_name },
        originCountryDisabled: true,
        destinationCountryCode: { key: editData?.destinationLocationDetails?.countryCode || editData?.destinationLocationDetails?.country_code, label: editData?.destinationLocationDetails?.countryName || editData?.destinationLocationDetails?.country_name },
        destinationCountryDisabled: true,
      }),
    }
  }
}